import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { EntityId } from "@bbwt/interfaces";
import { HttpResponsesHandlersFactory, IHttpResponseHandlerSettings } from "@bbwt/modules/data-service";
import { IPagedData, PagedCrudService } from "@features/grid";
import { Treatment } from "./treatment";
import { WalkIn } from "./walk-in";
import { NotificationService } from "@main/notification/notification.service";
import { NotificationType } from "@main/notification/notification-type";
import { IQueryCommand, ISimpleFilterInfoBase } from "@features/filter";
import { WalkInOfflineStorage } from "./walk-in-offline.storage";
import { NoteService } from "@project/note-shared/classes/note.service";
import { AppOnlineStateService } from "@main/pwa/app-online-state.service";
import { FileDetails } from "@main/file-storage";
import { IDocument_File } from "@project/models/episodes/document_file_d";
import { IEpisode_Document } from "@project/models/episodes/episode_document";
import { trimTime } from "@bbwt/utils/date-utils";
import { DocumentState } from "@project/models/episodes/document-state";
import { Note } from "@project/models/notes/note";
import { NoteState } from "@project/models/notes/note-state";
import {
    isRoutineTreatmentEpisode,
    isSelfDirectedGymRecoveryEpisode
} from "@project/episode/episode-select/episode-defaults";
import { NoteTreatment } from "@project/models/notes/note-treatment";
// import { formatQuickNoteTreatmentNames } from "./walk-in-treatment.util";
import { HistoricalItem } from "@mipro/classes/historical-item";

@Injectable({
    providedIn: "root"
})
export class WalkInService extends PagedCrudService<WalkIn> {
    readonly entityTitle = "Walk In";
    readonly url = "api/walk-ins";

    constructor(
        http: HttpClient,
        handlersFactory: HttpResponsesHandlersFactory,
        private notificationService: NotificationService,
        private noteService: NoteService,
        private appOnlineStateService: AppOnlineStateService
    ) {
        super(http, handlersFactory);
    }

    getPage(queryCommand: IQueryCommand, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<IPagedData<WalkIn>> {
        return super.getPage(queryCommand, responseHandlerSettings);
    }

    getByPatientId(patientId: EntityId): Promise<WalkIn> {
        return this.handleRequest<WalkIn>(
            this.http.get<WalkIn>(`${this.url}/get-by-patient/${patientId}`),
            this.handlersFactory.getForReadById("Self Registered Patient")
        );
    }

    public getTreatments(): Promise<Treatment[]> {
        return this.handleRequest<Treatment[]>(
            this.http.get<Treatment[]>(`${this.url}/get-treatments`),
            this.handlersFactory.getForReadAll()
        );
    }

    public approve(id: EntityId): Promise<any> {
        return this.handleRequest<WalkIn>(
            this.http.post<WalkIn>(`${this.url}/${id}/approve`, id),
            this.handlersFactory.getForUpdate("Walk In", { successMessage: "Walk In has been approved" })
        );
    }

    public approveSelfRegWalkIn(id: EntityId): Promise<any> {
        return this.handleRequest<WalkIn>(
            this.http.post<WalkIn>(`${this.url}/${id}/approve-self-reg`, id),
            this.handlersFactory.getForUpdate("Self Registered Patient", { successMessage: "Self Registered Patient has been approved" })
        );
    }

    public approveByEpisode(episodeId: EntityId): Promise<any> {
        return this.handleRequest<WalkIn>(
            this.http.post<WalkIn>(`${this.url}/approve-by-episode/${episodeId}`, episodeId),
            this.handlersFactory.getForUpdate("Walk In Draft Episode", { successMessage: "The draft episode has been approved" })
        );
    }

    async get(id: EntityId, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<WalkIn> {
        const walkIn: WalkIn = WalkInOfflineStorage.get(id as string);
        return walkIn == null ? super.get(id, responseHandlerSettings) : walkIn;
    }

    async create(item: WalkIn, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<WalkIn> {
        if (this.appOnlineStateService.isAppOnline) {
            // Quick Note is created in the backend, so we send only the other notes
            if (item.notes) {
                item.notes = item.notes.filter(n => n.id != null);
                // Make sure non-quick notes will be created in the backend
                item.notes.forEach(n => (n.id = null));
            }

            return super.create(item, responseHandlerSettings);
        } else {
            item.savedOffline = true;
            item.id = null;

            if (item.selectedTreatments?.length || item.additionalNotes) {
                item.notes = [this.getOfflineFirstQuickNote(item)];
            }

            if (item.episode) {
                if (!item.episode.notes) {
                    item.episode.notes = [];
                }

                if (!item.episode.historical) {
                    item.episode.historical = [];
                }

                if (!item.episode.documents) {
                    item.episode.documents = [];
                }

                if (!item.episode.prescriptions) {
                    item.episode.prescriptions = [];
                }
            }

            const walkIn: WalkIn = WalkInOfflineStorage.save(item);

            const notification = await this.notificationService.createOffline({
                id: null,
                title: `Walk In List - DRAFT OFFLINE for ${item.patientFullName} pending upload`,
                body: null,
                read: false,
                archived: false,
                created: new Date(),
                type: NotificationType.DraftWalkIn,
                redirectTo: "/app/walk-ins",
                redirectParams: [
                    {
                        name: "patientId",
                        value: walkIn.patientId.toString()
                    },
                    {
                        name: "walkInId",
                        value: walkIn.id
                    }
                ],
                savedOffline: true
            });

            walkIn.notificationId = notification.id;
            return WalkInOfflineStorage.update(walkIn);
        }
    }

    getHistoricalItems(walkInId: string): Promise<HistoricalItem[]> {
        return this.handleRequest<HistoricalItem[]>(
            this.http.get<HistoricalItem[]>(`${this.url}/get-historical-items/${walkInId}`),
            this.handlersFactory.getForReadAll());
    }

    getOfflineFirstQuickNote(walkIn: WalkIn): Note {
        const note = {
            id: null,
            episodeId: walkIn.episode?.id,
            noteFields: [],
            noteTreatments: [],
            selectedTreatments: [],
            createdByUserId: walkIn.clinicianUserId,
            createdByUserFullName: walkIn.clinicianUserFullName,
            createdByUserRole: walkIn.clinicianUserRole,
            createdByUserTitle: walkIn.clinicianUserTitle,
            walkInQuickNote: true,
            treatmentManuallySelected: walkIn.treatmentManuallySelected ?? false,
            state: NoteState.WalkInDraft,
            creationDate: walkIn.createDateTime ?? new Date()
        };

        let treatmentText = "";
        const bodyPartSuffix =
            (walkIn.bodyPart ?? "") != "" && walkIn.bodyPart != "N/A" ? " - " + walkIn.bodyPart : "";
        const bodySideSuffix =
            (walkIn.bodySide ?? "") != "" && walkIn.bodySide != "N/A" ? " - " + walkIn.bodySide : "";

        if (isRoutineTreatmentEpisode(walkIn.episode)) {
            treatmentText = `Routine treatment performed${bodyPartSuffix}${bodySideSuffix}`;
        } else if (isSelfDirectedGymRecoveryEpisode(walkIn.episode)) {
            treatmentText = `Self-Directed Gym/Recovery${bodyPartSuffix}${bodySideSuffix}`;
        }
        // Otherwise leave empty — treatments are shown in the note Treatments section

        let additionalNotes = (walkIn.additionalNotes ?? "") == "" ? "" : "Additional Notes:\n" + walkIn.additionalNotes;

        let quickNoteText = additionalNotes;
        if (treatmentText != "") {
            quickNoteText = treatmentText + (additionalNotes ? "\n\n" + additionalNotes : "");
        }

        const noteFields =
            quickNoteText != ""
                ? [
                      {
                          name: "clinicalNote",
                          value: quickNoteText,
                          label: "Quick Note"
                      }
                  ]
                : [];

        note.noteFields = noteFields;
        note.selectedTreatments = walkIn.selectedTreatments;
        note.noteTreatments = (note.selectedTreatments ?? []).map(
            nt =>
                ({
                    walkInId: walkIn.id,
                    treatmentId: nt.id,
                    treatmentName: nt.name
                } as NoteTreatment)
        );

        return note as Note;
    }

    getDateFilteredLocallyStored(date: Date): WalkIn[] {
        return WalkInOfflineStorage.getAll().filter(w => {
            w.createDateTime = new Date(w.createDateTime);
            return (
                w.createDateTime.getUTCDate() == date.getUTCDate() &&
                w.createDateTime.getUTCMonth() == date.getUTCMonth() &&
                w.createDateTime.getUTCFullYear() == date.getUTCFullYear()
            );
        });
    }

    getFilteredLocalItems(filters: ISimpleFilterInfoBase[]): WalkIn[] {
        return WalkInOfflineStorage.getAll().filter(w => {
            w.createDateTime = new Date(w.createDateTime);
            const requestedDate = filters.find(f => f.propertyName == "walkInsDate");
            const dateMatches =
                w.createDateTime.getUTCDate() == requestedDate.value.getUTCDate() &&
                w.createDateTime.getUTCMonth() == requestedDate.value.getUTCMonth() &&
                w.createDateTime.getUTCFullYear() == requestedDate.value.getUTCFullYear();
            if (dateMatches) {
                const requestedClinician = filters.find(f => f.propertyName == "userId");
                const requestedPatient = filters.find(f => f.propertyName == "patientId");
                return (
                    (!requestedClinician || w.clinicianUserId == requestedClinician.value) &&
                    (!requestedPatient || w.patientId == requestedPatient.value)
                );
            }
            return dateMatches;
        });
    }

    deleteLocallyStoredItem(id: string): WalkIn {
        const walkIn = WalkInOfflineStorage.remove(id);
        if (!walkIn) {
            return null;
        }

        this.notificationService.deleteOfflineNotification(walkIn.notificationId.toString());
        walkIn.notes?.forEach(n => this.noteService.deleteLocalNote(n.id));

        return walkIn;
    }

    async update(id: EntityId, item: WalkIn, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<WalkIn> {
        const walkIn: WalkIn = WalkInOfflineStorage.get(id as string);

        if (walkIn == null) {
            return super.update(id, item, responseHandlerSettings);
        }

        item.savedOffline = true;
        item.id = id as string;

        if (item.selectedTreatments?.length || item.additionalNotes) {
            const otherNotes = (item.notes ?? []).filter(n => !n.walkInQuickNote && n.id != null);
            item.notes = [this.getOfflineFirstQuickNote(item), ...otherNotes];
        }

        return WalkInOfflineStorage.update(item);
    }

    sortWalkIns(walkIns: WalkIn[]): WalkIn[] {
        return walkIns.sort((a, b) => {
            if (a.createDateTime > b.createDateTime) {
                return -1;
            }
            if (a.createDateTime < b.createDateTime) {
                return 1;
            }
            return 0;
        });
    }

    createWalkInFiles(walkIn: WalkIn, documents: FileDetails[], patientId, createDateTime?: Date): IEpisode_Document {
        const files = documents.map(
            file =>
                <IDocument_File>{
                    id: "0",
                    episode_DocumentId: "0",
                    fileDetailsId: file.id,
                    fileCreatedTime: file.uploadTime,
                    fileDetails: null,
                    isArchived: false,
                    createdByFullName: ""
                }
        );

        const documentRibbonId = this.findLastRibbonDocumentId(walkIn, createDateTime);

        const episodeDocument = <IEpisode_Document>{
            id: documentRibbonId ?? "0",
            episodeId: walkIn.episode.id,
            patientId: patientId,
            uploadTime: createDateTime,
            state: DocumentState.WalkInDraft,
            walkInId: walkIn.id,
            document_Files: files
        };
        return episodeDocument;
    }

    private findLastRibbonDocumentId(walkIn: WalkIn, date: Date = null) {
        let allSubEntities: { id?: string; time: Date }[] = [
            ...(walkIn.documents?.map(d => ({ id: d.id, time: new Date(d.uploadTime as any) })) ?? []),
            ...(walkIn.prescriptions?.map(p => ({ time: p.prescriptionLines[0]?.prescribedDateTime })) ?? []),
            ...(walkIn.notes?.map(n => ({ time: new Date(n.creationDate) })) ?? [])
        ];

        if (date) {
            allSubEntities = allSubEntities.filter(x => trimTime(x.time).getTime() == trimTime(date).getTime());
        }

        const last = allSubEntities.reduce((p, c) => (p?.time == null || p?.time < c?.time ? c : p), null);
        return last?.id;
    }
}
